import React, { useState, useEffect } from 'react';
import { Users, Target, ArrowRight, ArrowLeft, Check, Edit3 } from 'lucide-react';
import { MatchplayMatch, MatchplayTournament, MatchplayPlayer, UpdateHoleRequest } from '../../../types/matchplay-v2';
import { MatchplayScoring } from '../../../lib/matchplay-v2/scoring';
import { getParValues } from '../../../lib/matchplay-v2/utils';
import { HoleInput } from './HoleInput';

interface FoursomesScoringProps {
  match: MatchplayMatch;
  tournament: MatchplayTournament;
  team1Players: MatchplayPlayer[];
  team2Players: MatchplayPlayer[];
  currentHole: number;
  onHoleSubmit: (holeData: UpdateHoleRequest) => void;
  onHoleNavigation: (holeNumber: number) => void;
  isLoading: boolean;
}

export function FoursomesScoring({
  match,
  tournament,
  team1Players,
  team2Players,
  currentHole,
  onHoleSubmit,
  onHoleNavigation,
  isLoading
}: FoursomesScoringProps) {
  // All hooks must be called first, before any conditional logic
  const [editingHole, setEditingHole] = useState<number | null>(null);
  const [tempScore1, setTempScore1] = useState<number | null>(null);
  const [tempScore2, setTempScore2] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isHoleSelectionMode, setIsHoleSelectionMode] = useState(false);
  
  // Local state for current hole scores
  const [currentHoleScore1, setCurrentHoleScore1] = useState<number | null>(null);
  const [currentHoleScore2, setCurrentHoleScore2] = useState<number | null>(null);

  // Default par values for 18 holes
  const defaultPars = getParValues(tournament.courseId);

  // Load current hole scores when currentHole changes
  useEffect(() => {
    const hole = match.holes.find(h => h.holeNumber === currentHole);
    if (hole) {
      setCurrentHoleScore1(hole.team1GrossScores[0] || null);
      setCurrentHoleScore2(hole.team2GrossScores[0] || null);
    } else {
      setCurrentHoleScore1(null);
      setCurrentHoleScore2(null);
    }
  }, [currentHole, match.holes]);

  // Safety checks for player arrays - AFTER all hooks
  if (!team1Players || team1Players.length !== 2 || !team2Players || team2Players.length !== 2) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>Error: Foursomes requires exactly 2 players per team</p>
      </div>
    );
  }

  // Get scores for all holes (one score per team in foursomes)
  const getHoleScore = (holeNumber: number, teamIndex: number) => {
    const hole = match.holes.find(h => h.holeNumber === holeNumber);
    if (!hole) return null;
    return teamIndex === 0 ? hole.team1GrossScores[0] : hole.team2GrossScores[0];
  };

  // Calculate totals
  const calculateTotal = (holes: number[], teamIndex: number) => {
    let total = 0;
    let count = 0;
    holes.forEach(holeNum => {
      const score = getHoleScore(holeNum, teamIndex);
      if (score) {
        total += score;
        count++;
      }
    });
    return count > 0 ? total : null;
  };

  const frontNine = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const backNine = [10, 11, 12, 13, 14, 15, 16, 17, 18];
  
  const team1FrontTotal = calculateTotal(frontNine, 0);
  const team1BackTotal = calculateTotal(backNine, 0);
  const team1Total = team1FrontTotal && team1BackTotal ? team1FrontTotal + team1BackTotal : null;
  
  const team2FrontTotal = calculateTotal(frontNine, 1);
  const team2BackTotal = calculateTotal(backNine, 1);
  const team2Total = team2FrontTotal && team2BackTotal ? team2FrontTotal + team2BackTotal : null;

  const handleHoleClick = (holeNumber: number) => {
    if (holeNumber <= currentHole) {
      setEditingHole(holeNumber);
      const score1 = getHoleScore(holeNumber, 0);
      const score2 = getHoleScore(holeNumber, 1);
      setTempScore1(score1);
      setTempScore2(score2);
    }
  };

  const handleHoleSelection = (holeNumber: number) => {
    if (isHoleSelectionMode) {
      onHoleNavigation(holeNumber);
      setIsHoleSelectionMode(false);
    } else {
      handleHoleClick(holeNumber);
    }
  };

  const handleScoreUpdate = () => {
    if (editingHole === null || tempScore1 === null || tempScore2 === null) return;

    const holeData: UpdateHoleRequest = {
      holeNumber: editingHole,
      team1GrossScores: [tempScore1],
      team2GrossScores: [tempScore2]
    };

    onHoleSubmit(holeData);
    setEditingHole(null);
    setTempScore1(null);
    setTempScore2(null);
  };

  const handleCancelEdit = () => {
    setEditingHole(null);
    setTempScore1(null);
    setTempScore2(null);
    setErrors({});
  };

  // Submit current hole scores
  const handleSubmitCurrentHole = () => {
    // Use par values as defaults when null
    const score1 = currentHoleScore1 !== null ? currentHoleScore1 : defaultPars[currentHole - 1];
    const score2 = currentHoleScore2 !== null ? currentHoleScore2 : defaultPars[currentHole - 1];

    const holeData: UpdateHoleRequest = {
      holeNumber: currentHole,
      team1GrossScores: [score1],
      team2GrossScores: [score2]
    };

    onHoleSubmit(holeData);
    setErrors({});
  };

  // Check if current hole is already completed
  const isCurrentHoleCompleted = () => {
    const hole = match.holes.find(h => h.holeNumber === currentHole);
    return hole !== undefined;
  };

  // Mobile-friendly hole cell component
  const TeamHoleCell = ({ holeNumber, teamIndex, isCurrentHole }: {
    holeNumber: number;
    teamIndex: number;
    isCurrentHole: boolean;
  }) => {
    const par = defaultPars[holeNumber - 1];
    const isClickable = isHoleSelectionMode || holeNumber <= currentHole;
    const score = getHoleScore(holeNumber, teamIndex);

    return (
      <button
        onClick={() => isClickable && handleHoleSelection(holeNumber)}
        disabled={!isClickable}
        className={`h-8 w-8 text-xs font-bold rounded transition-all touch-manipulation ${
          isCurrentHole
            ? 'bg-primary-500 text-white shadow-md'
            : isHoleSelectionMode
            ? 'bg-yellow-100 text-yellow-700 border border-yellow-300 hover:bg-yellow-200'
            : score === null
            ? 'bg-gray-100 text-gray-400 border border-gray-200'
            : score === par
            ? teamIndex === 0 ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-red-100 text-red-700 border border-red-300'
            : score < par
            ? 'bg-green-100 text-green-700 border border-green-300'
            : teamIndex === 0 ? 'bg-blue-200 text-blue-800 border border-blue-400' : 'bg-red-200 text-red-800 border border-red-400'
        } ${isClickable ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
      >
        {score || '-'}
      </button>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full max-w-none mx-auto p-2 space-y-4">
        
        {/* Hole Selection Button - Above Current Hole */}
        <div className="flex justify-center">
          <button
            onClick={() => setIsHoleSelectionMode(!isHoleSelectionMode)}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${
              isHoleSelectionMode
                ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {isHoleSelectionMode ? 'Cancel Selection' : 'Select Hole'}
          </button>
        </div>
        
        {/* Current Hole Quick Entry - Moved to Top */}
        {currentHole <= 18 && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 w-full">
            <h3 className="font-semibold text-primary-900 mb-4 text-center">
              Hole {currentHole} (Par {defaultPars[currentHole - 1]}) - Foursomes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="bg-blue-50 rounded-lg p-4">
                <HoleInput
                  label="Team A Score"
                  value={currentHoleScore1}
                  onChange={setCurrentHoleScore1}
                  par={defaultPars[currentHole - 1]}
                  disabled={isLoading}
                />
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <HoleInput
                  label="Team B Score"
                  value={currentHoleScore2}
                  onChange={setCurrentHoleScore2}
                  par={defaultPars[currentHole - 1]}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex flex-col items-center gap-3">
              {errors.submit && (
                <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
              )}
              
              <button
                onClick={handleSubmitCurrentHole}
                disabled={isLoading}
                className="w-full max-w-md px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg touch-manipulation active:scale-95 transition-all shadow-md"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </div>
                ) : (
                  `${isCurrentHoleCompleted() ? 'Update' : 'Submit'} Hole ${currentHole} Scores`
                )}
              </button>
            </div>
          </div>
        )}

        {/* Team Headers - Compact */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-blue-50 rounded-lg p-2 text-center">
            <h3 className="font-semibold text-blue-900 text-sm">Team A</h3>
            <div className="text-xs text-blue-700">
              {team1Players[0].playerName.split(' ')[0]} & {team1Players[1].playerName.split(' ')[0]}
            </div>
            <div className="text-xs text-blue-600 mt-1">Alternate Shot</div>
          </div>
          <div className="bg-red-50 rounded-lg p-2 text-center">
            <h3 className="font-semibold text-red-900 text-sm">Team B</h3>
            <div className="text-xs text-red-700">
              {team2Players[0].playerName.split(' ')[0]} & {team2Players[1].playerName.split(' ')[0]}
            </div>
            <div className="text-xs text-red-600 mt-1">Alternate Shot</div>
          </div>
        </div>

        {/* Scorecard Layout - Full Width Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-md p-2 w-full">
          <div className="space-y-4">
            
            {/* Front Nine */}
            <div className="w-full">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">Front Nine</h3>
              
              {/* Hole Numbers */}
              <div className="grid grid-cols-11 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-gray-600 p-1">Hole</div>
                {frontNine.map(hole => (
                  <div key={hole} className="text-xs font-medium text-gray-600 p-1">{hole}</div>
                ))}
                <div className="text-xs font-medium text-gray-600 p-1">IN</div>
              </div>

              {/* Par Row */}
              <div className="grid grid-cols-11 gap-px mb-2 text-center">
                <div className="text-xs text-gray-500 p-1">Par</div>
                {frontNine.map(hole => (
                  <div key={hole} className="text-xs text-gray-500 p-1">{defaultPars[hole - 1]}</div>
                ))}
                <div className="text-xs text-gray-500 p-1 font-semibold">{frontNine.reduce((sum, hole) => sum + defaultPars[hole - 1], 0)}</div>
              </div>

              {/* Team A Row */}
              <div className="grid grid-cols-11 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-blue-700 p-1">Team A</div>
                {frontNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={0} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-blue-50 rounded p-1">
                  {team1FrontTotal || '-'}
                </div>
              </div>

              {/* Team B Row */}
              <div className="grid grid-cols-11 gap-px text-center">
                <div className="text-xs font-medium text-red-700 p-1">Team B</div>
                {frontNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={1} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-red-50 rounded p-1">
                  {team2FrontTotal || '-'}
                </div>
              </div>
            </div>

            {/* Back Nine */}
            <div className="w-full">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">Back Nine</h3>
              
              {/* Hole Numbers */}
              <div className="grid grid-cols-12 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-gray-600 p-1">Hole</div>
                {backNine.map(hole => (
                  <div key={hole} className="text-xs font-medium text-gray-600 p-1">{hole}</div>
                ))}
                <div className="text-xs font-medium text-gray-600 p-1">OUT</div>
                <div className="text-xs font-medium text-gray-600 p-1">Total</div>
              </div>

              {/* Par Row */}
              <div className="grid grid-cols-12 gap-px mb-2 text-center">
                <div className="text-xs text-gray-500 p-1">Par</div>
                {backNine.map(hole => (
                  <div key={hole} className="text-xs text-gray-500 p-1">{defaultPars[hole - 1]}</div>
                ))}
                <div className="text-xs text-gray-500 p-1 font-semibold">{backNine.reduce((sum, hole) => sum + defaultPars[hole - 1], 0)}</div>
                <div className="text-xs text-gray-500 p-1 font-semibold">72</div>
              </div>

              {/* Team A Row */}
              <div className="grid grid-cols-12 gap-px mb-1 text-center">
                <div className="text-xs font-medium text-blue-700 p-1">Team A</div>
                {backNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={0} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-blue-50 rounded p-1">
                  {team1BackTotal || '-'}
                </div>
                <div className="text-xs font-bold bg-blue-100 rounded p-1">
                  {team1Total || '-'}
                </div>
              </div>

              {/* Team B Row */}
              <div className="grid grid-cols-12 gap-px text-center">
                <div className="text-xs font-medium text-red-700 p-1">Team B</div>
                {backNine.map(hole => (
                  <div key={hole} className="flex justify-center p-1">
                    <TeamHoleCell 
                      holeNumber={hole} 
                      teamIndex={1} 
                      isCurrentHole={hole === currentHole}
                    />
                  </div>
                ))}
                <div className="text-xs font-bold bg-red-50 rounded p-1">
                  {team2BackTotal || '-'}
                </div>
                <div className="text-xs font-bold bg-red-100 rounded p-1">
                  {team2Total || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Score Editing Modal */}
        {editingHole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">Edit Hole {editingHole}</h3>
                <p className="text-sm text-gray-600 mt-1">Par {defaultPars[editingHole - 1]} - Foursomes (Alternate Shot)</p>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <HoleInput
                    label="Team A Score"
                    value={tempScore1}
                    onChange={setTempScore1}
                    par={defaultPars[editingHole - 1]}
                    error={errors.team1}
                    disabled={isLoading}
                  />
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <HoleInput
                    label="Team B Score"
                    value={tempScore2}
                    onChange={setTempScore2}
                    par={defaultPars[editingHole - 1]}
                    error={errors.team2}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScoreUpdate}
                  disabled={isLoading || tempScore1 === null || tempScore2 === null}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Rules - Collapsible on Mobile */}
        <details className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
          <summary className="p-4 cursor-pointer font-semibold text-blue-900 hover:bg-blue-100 transition-colors touch-manipulation">
            Foursomes Rules (Tap to expand)
          </summary>
          <div className="px-4 pb-4 border-t border-blue-200">
            <ul className="text-sm text-blue-800 space-y-2 mt-3">
              <li>• Tap any completed hole to edit the team score</li>
              <li>• Partners alternate shots with one ball</li>
              <li>• One score per team per hole</li>
              <li>• Par values are pre-filled for quick entry</li>
              <li>• Green = Under par, Blue/Red = Par or over par by team</li>
            </ul>
          </div>
        </details>
      </div>
    </div>
  );
} 